import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors } from '@angular/forms';
import { Store } from '@ngrx/store';
import { OAuthService } from 'angular-oauth2-oidc';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AuditLog } from 'src/app/core/models/auditLog';
import { TenantService } from 'src/app/core/services/tenant.service';
import { SessionService } from 'src/app/shared/services/session.service';
import { getLoggedInUserInfo } from 'src/app/state';
import { State } from 'src/app/state/app.state';
import { TenantModel } from '../../../../core/models/tenant';
import { getAvailableTenants, getCurrentTenant } from './../../../../state/index';

@Component({
  selector: 'app-tenantchange-popup',
  templateUrl: './app-tenantchange-popup.component.html',
  styleUrls: ['./app-tenantchange-popup.component.scss']
})
export class AppTenantChangeComponent implements OnInit, OnDestroy {

  @Input() public dialogOpened?: boolean;
  @Output() public dialogOpenedChange: EventEmitter<boolean> = new EventEmitter(false);
  @Output() public cancelled = new EventEmitter();
  // List of tenants user can be logged into
  public availableTenants: TenantModel[] = [];
  // property handles the tenant change functionality
  public showTenantChangeOption: boolean = false;
  // Currently authenticated tenant
  public selectedTenant: TenantModel = {
    isDefault: false,
    tenantID: 0,
    tenantDisplayName: ''
  };

  // Create the form for the popup
  public tenantchangeForm: FormGroup = new FormGroup({
    tenant: new FormControl(''),
    token: new FormControl('')
  });

  public loggedInUserId: string = '';

  private subs: Subscription = new Subscription();

  constructor(
    private store: Store<State>,
    private oauthService: OAuthService,
    private session: SessionService,
    private tenantService: TenantService) { }

  ngOnInit(): void {
    // initialize default values
    this.availableTenants = [];
    this.showTenantChangeOption = true;
    this.subs = new Subscription();
    this.store.select(getLoggedInUserInfo).subscribe(info => this.loggedInUserId = info?.externalUserId || '');

    // Assign the custom form group validator
    this.tenantchangeForm.setValidators(this._validator.bind(this));

    const selectedTenantSub = this.store.select(getCurrentTenant)
      .pipe(filter(tenant => tenant !== null))
      .subscribe(tenant => this._onSelectedTenantChange(tenant));
    const availableTenantsSub = this.store.select(getAvailableTenants)
      .subscribe(tenants => this._onAvailableTenantsChange(tenants));

    this.subs.add(selectedTenantSub);
    this.subs.add(availableTenantsSub);

  }

  /// This validator should be in invalid state if a no token provided for non default tenant
  /// Client token is required for non default clients
  private _validator(group: AbstractControl): ValidationErrors | null {
    const token = (group as FormGroup).controls.token;
    token.setErrors(null);
    return null;
  }

  // Explicitly call this method to show update the visibility of the elements updated with properties
  // Added this method to increase the performance
  private _refreshShowHideElements(): void {
    if (this.availableTenants.length > 0) {
      this.showTenantChangeOption = true;
    } else {
      this.showTenantChangeOption = false;
    }
  }

  private _onAvailableTenantsChange(tenants: TenantModel[]): void {
    this.availableTenants = tenants;
    this._refreshShowHideElements();
  }

  private _onSelectedTenantChange(tenant: TenantModel): void {
    this.selectedTenant = tenant;
    this._resetFormValues();
  }

  private _resetFormValues() {
    this.tenantchangeForm?.patchValue(
      {
        tenant: this.selectedTenant.tenantID,
        token: ''
      }
    );
    this.tenantchangeForm?.markAsPristine();
    this.tenantchangeForm?.markAsUntouched();
    this.tenantchangeForm?.updateValueAndValidity();
  }

  private _changeTenant() {
    const selectedTenantId = this.tenantchangeForm?.value.tenant;
    let token = this.tenantchangeForm?.value.token;
    //Always sending default value if token is Null
    if (token == null || token == "") {
      token = "N/A";
    }
    const selectedTenant = this.availableTenants.find(t => t.tenantID === selectedTenantId);
    // Save the tenant as the switch code to local storage to request token on next refresh
    localStorage.setItem('tenant', JSON.stringify(selectedTenant));
    // Save the ticket code as the switch code to local storage to request token on next refresh
    localStorage.setItem('switch_code', `${token}`);

    if (selectedTenant && selectedTenant.isDefault != true) {
      const auditLog = new AuditLog(selectedTenantId, this.loggedInUserId, `${token}`);
      this.tenantService.logTenantSwitch(auditLog).subscribe();
    }

    localStorage.removeItem('access_token');
    this.session.clearSignInSession();
    this.oauthService.initCodeFlow();
    this.oauthService.loadDiscoveryDocument().then();
  }

  // FormControl Getters
  get tokenField() {
    return this.tenantchangeForm?.get('token');
  }
  // FormControl Getters END


  /// -- Handlers
  public onSaveClicked(): void {
    this._changeTenant();
  }

  public tenantTrackBy(index: number, tenant: TenantModel) {
    return tenant.tenantID;
  }

  public close() {
    this.cancelled.emit();
    this.dialogOpened = false;
  }

  public open() {
    this.dialogOpened = true;
  }

  // -- Handlers end
  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }
}
